import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { getAccessFlowLock } from "@/lib/access-flow";

type VerificationState = "checking" | "verified" | "required" | "blocked";

/**
 * Barreira de navegação para recursos sociais 18+.
 * A proteção efetiva permanece no banco; este componente evita que a pessoa
 * chegue a uma tela que o servidor obrigatoriamente recusará.
 */
const AgeVerificationGuard = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const [state, setState] = useState<VerificationState>("checking");

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (getAccessFlowLock() && getAccessFlowLock() !== "user") {
        if (!cancelled) setState("verified");
        return;
      }

      const { data, error } = await supabase.rpc("get_own_profile");
      const row = Array.isArray(data) ? data[0] : data;
      if (cancelled) return;

      if (error || !row) {
        // Falha de rede não é evidência de ausência de verificação. O banco
        // continua negando qualquer operação social para contas não verificadas.
        setState("verified");
        return;
      }

      const accountStatus = (row as { account_status?: string }).account_status;
      const ageVerified = (row as { age_verified?: boolean }).age_verified === true;
      if (["minor_blocked", "suspended_minority", "banned"].includes(accountStatus ?? "")) {
        setState("blocked");
      } else {
        setState(ageVerified && accountStatus === "age_verified" ? "verified" : "required");
      }
    };

    void check();
    return () => { cancelled = true; };
  }, []);

  if (state === "checking") {
    return (
      <div className="min-h-dvh grid place-items-center bg-background" aria-live="polite">
        <Loader2 className="h-7 w-7 animate-spin text-primary" aria-label="Verificando acesso" />
      </div>
    );
  }
  if (state === "blocked") return <Navigate to="/minor-blocked" replace />;
  if (state === "required") {
    const from = `${location.pathname}${location.search}`;
    return <Navigate to={`/verify-age?from=${encodeURIComponent(from)}`} replace />;
  }
  return <>{children}</>;
};

export default AgeVerificationGuard;